// Where this marina is (Settings › Yard profile › Location), stored at /settings/location:
//   { lat, lng, timeZone, tideStation, tideStationName, updatedAt, updatedBy }
// Tides, weather, map starting points and the marina's clock all read it, so the same
// app works for any marina. Kept on the device too (localStorage) so screens can paint
// before the database answers and while offline.
//
// Nothing here falls back to another marina's location: with no location set, tide and
// weather show a "set your location" message instead of somebody else's harbor.

import { auth, database } from './initFirebase';
import { ref, get } from 'firebase/database';

const KEY = 'marinaLocation';

const browserTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

function clean(v) {
  if (!v || typeof v !== 'object') return null;
  const lat = Number(v.lat);
  const lng = Number(v.lng);
  return {
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    timeZone: v.timeZone || null,
    tideStation: v.tideStation ? String(v.tideStation) : null,
    tideStationName: v.tideStationName || '',
  };
}

/** The last location this device saw (null before the first load). Synchronous. */
export function cachedLocation() {
  try { return clean(JSON.parse(localStorage.getItem(KEY) || 'null')); } catch { return null; }
}

// Resolves once someone is signed in (pages ask for tides before the sign-in screen is done).
const signedIn = () => new Promise(resolve => {
  const off = auth.onAuthStateChanged(u => { if (u) { off(); resolve(u); } });
});

let pending = null;
/**
 * The marina's location; null when not set. With a copy on this device it answers at once
 * and refreshes from the database in the background; otherwise it waits for sign-in.
 */
export function loadLocation({ fresh = false } = {}) {
  const local = cachedLocation();
  if (local && !fresh) {
    if (!pending) fetchLocation();
    return Promise.resolve(local);
  }
  if (pending && !fresh) return pending;
  return fetchLocation();
}

function fetchLocation() {
  pending = signedIn()
    .then(() => get(ref(database, 'settings/location')))
    .then(snap => {
      const loc = clean(snap.val());
      try { localStorage.setItem(KEY, JSON.stringify(loc)); } catch { /* private mode */ }
      return loc;
    })
    .catch(e => {
      console.warn('[location] using the copy on this device:', e?.message || e);
      pending = null; // try the database again next time
      return cachedLocation();
    });
  return pending;
}

/** The marina's time zone (IANA name). Synchronous; the device's own zone until one is set. */
export function marinaTimeZone() {
  return cachedLocation()?.timeZone || browserTimeZone();
}

/** { lat, lng } of the marina, or null when not set. */
export async function marinaLatLng() {
  const l = await loadLocation();
  return l && l.lat != null && l.lng != null ? { lat: l.lat, lng: l.lng } : null;
}

/** The NOAA tide-prediction station id, or null when not set. */
export async function tideStation() {
  return (await loadLocation())?.tideStation || null;
}

/** Map starting point: the marina's location, else `fallback` ({ Lat, Lng }). Synchronous. */
export function mapCenter(fallback) {
  const l = cachedLocation();
  return l && l.lat != null && l.lng != null ? { Lat: l.lat, Lng: l.lng } : fallback;
}

export const NO_LOCATION_MSG = 'Set the marina’s location and tide station in Settings › Yard profile.';
